import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import {Alert, Button, Card} from '@betareader/design-system';
import {listMine, deleteSample} from './api';

const List = styled.ul`
  display: grid;
  gap: var(--br-space-3);
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Item = styled.li`
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--br-space-2);
  margin-top: var(--br-space-3);
`;

const Stats = styled.p`
  color: var(--br-muted);
  display: flex;
  flex-wrap: wrap;
  gap: var(--br-space-3);
`;

const LinkButton = ({to, children, variant = 'secondary'}) => (
  <Link className={`br-button br-button--${variant} br-button--small`} to={to}>{children}</Link>
);

const MySampleChapterList = () => {
  const idToken = useSelector(state => state.user.idToken);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!idToken) return;
    try {
      setError(null);
      const data = await listMine(idToken);
      setItems(Array.isArray(data) ? data : []);
    } catch (cause) {
      setError(cause.message);
    }
  }, [idToken]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async id => {
    if (!window.confirm('Delete this sample chapter and all its ratings?')) return;
    try {
      await deleteSample(idToken, id);
      await load();
    } catch (cause) {
      setError(cause.message);
    }
  };

  return (
    <div>
      {error && <Alert variant='error'>{error}</Alert>}
      {items === null && <p role='status'>Loading sample chapters…</p>}
      {items?.length === 0 && (
        <Alert variant='info'>
          You haven't created any sample chapters yet.{' '}
          <Link to='/sample-chapters/new'>Create your first sample</Link>.
        </Alert>
      )}
      {items?.length > 0 && (
        <List>
          {items.map(sample => (
            <Item key={sample._id}>
              <Card style={{padding: 'var(--br-space-3)'}}>
                <h2><Link to={`/sample-chapters/edit/${sample._id}`}>{sample.title}</Link></h2>
                <p>Variant {sample.variantLabel} · {sample.listed ? 'Listed' : 'Draft'} · {sample.audioUrl ? 'Audio' : 'Text only'}</p>
                <Stats>
                  <span>{sample.ratingsCount || 0} ratings</span>
                  <span>{sample.yesCount || 0} positive</span>
                  <span>{sample.noCount || 0} negative</span>
                  <span>{sample.contactOptInCount || 0} contact opt-ins</span>
                </Stats>
                <Actions>
                  <LinkButton to={`/sample-chapters/read/${sample._id}`}>Preview</LinkButton>
                  <LinkButton to={`/sample-chapters/edit/${sample._id}`}>Edit</LinkButton>
                  <Button size='small' variant='danger' onClick={() => handleDelete(sample._id)}>
                    Delete
                  </Button>
                </Actions>
              </Card>
            </Item>
          ))}
        </List>
      )}
    </div>
  );
};

export {LinkButton};
export default MySampleChapterList;
